import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaSave, FaTags } from 'react-icons/fa';
import { showSuccessToast, showErrorToast } from '../../../shared/utils/toastHelper';
import apiClient from '../../../shared/utils/apiConfig';

const AdminSubSubCategoryEdit = () => {
    const { setTitle } = useTitle();
    const { subCategoryId, subSubCategoryId } = useParams();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        subcategory_id: subCategoryId,
        name_ar: '',
        name_en: ''
    });
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        setTitle('تعديل الفئة الفرعية');
    }, [setTitle]);

    useEffect(() => {
        fetchSubSubCategoryDetails();
    }, [subSubCategoryId]);

    const fetchSubSubCategoryDetails = async () => {
        try {
            setInitialLoading(true);
            
            const response = await apiClient.get(`/admin/sub_category/sub-sub-categories/${subSubCategoryId}`);
            
            if (response.data.status === 'success' || response.data.statusCode === 200) {
                const subSubCategory = response.data.data || response.data;
                setFormData({
                    subcategory_id: subCategoryId,
                    name_ar: subSubCategory.name_ar || '',
                    name_en: subSubCategory.name_en || ''
                });
            } else {
                throw new Error(response.data.message || 'فشل في جلب تفاصيل الفئة الفرعية');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل الفئة الفرعية');
            navigate(`/admin-categories/${subCategoryId}/sub-sub-categories`);
        } finally {
            setInitialLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name_ar.trim() || !formData.name_en.trim()) {
            showErrorToast('يرجى ملء الاسم بالعربية والإنجليزية');
            return;
        }

        try {
            setLoading(true);
            
            await apiClient.put(`/admin/sub_category/sub-sub-categories/${subSubCategoryId}`, formData);
            
            showSuccessToast('تم تحديث الفئة الفرعية بنجاح');
            navigate(`/admin-categories/${subCategoryId}/sub-sub-categories`);
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في تحديث الفئة الفرعية');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل تفاصيل الفئة الفرعية...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-outline-secondary mx-3"
                            onClick={() => navigate(`/admin-categories/${subCategoryId}/sub-sub-categories`)}>
                            <FaArrowLeft className="mx-2" />
                            العودة
                        </motion.button>
                        <h4 className="mb-0">تعديل الفئة الفرعية</h4>
                    </div>
                </div>

                {/* Form */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-4">
                                    <label className="form-label">
                                        <FaTags className="mx-2" />
                                        الاسم (عربي) *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleInputChange}
                                        placeholder="أدخل الاسم باللغة العربية..."
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-4">
                                    <label className="form-label">
                                        <FaTags className="mx-2" />
                                        الاسم (إنجليزي) *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_en"
                                        value={formData.name_en}
                                        onChange={handleInputChange}
                                        placeholder="Enter the name in English..."
                                        required
                                    />
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-3">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => navigate(`/admin-categories/${subCategoryId}/sub-sub-categories`)}>
                                    إلغاء
                                </motion.button>
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={loading}>
                                    {loading ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm mx-2" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            جاري التحديث...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave className="mx-2" />
                                            تحديث الفئة الفرعية
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSubSubCategoryEdit;
